/**
 * libpq keyword/value connection-string parser → pgsql.
 *
 * Example: host=db.internal port=5432 dbname=orders user=app sslmode=require
 * Values may be single-quoted (password='p a ss'), with libpq's backslash escapes; braces are literal.
 */

import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import type { ConnectionStringParser, ParserMatch } from './types';
import { scanLibpqConninfo, type KeyValuePair } from '@/core/utils/connectionStringTokens';

const LIBPQ_KEYS = new Set([
  'host',
  'hostaddr',
  'port',
  'dbname',
  'user',
  'password',
  'sslmode',
  'application_name',
  'connect_timeout',
  'options',
  'passfile',
  'service',
  'target_session_attrs',
]);

export const parseLibpq: ConnectionStringParser = (input) => {
  const trimmed = input.trim();
  // A URL scheme or `jdbc:` prefix rules it out; the pairs are read exactly as libpq's conninfo_parse reads them (C3
  // reviews r3 C3-15, r5 C3-21) — `null` where libpq rejects the text.
  if (/^[a-z][a-z0-9+.-]*:\/\//iu.test(trimmed) || /^jdbc:/iu.test(trimmed)) return null;
  const pairs = scanLibpqConninfo(trimmed);
  return pairs ? libpqMatch(pairs) : null;
};

export function libpqMatch(pairs: readonly KeyValuePair[]): ParserMatch | null {
  if (pairs.length === 0) return null;
  // Require at least one recognised libpq key so we don't grab random text.
  if (!pairs.some((p) => LIBPQ_KEYS.has(p.key.toLowerCase()))) return null;

  const values: Partial<ConnectionFormValues> = { engine: 'pgsql' };
  const matchedFields: string[] = [];
  const unmatchedKeys: string[] = [];

  for (const { key, value } of pairs) {
    const lk = key.toLowerCase();
    switch (lk) {
      case 'host':
      case 'hostaddr':
        values.server = value;
        matchedFields.push('server');
        break;
      case 'port':
        values.port = value;
        matchedFields.push('port');
        break;
      case 'dbname':
        values.database = value;
        matchedFields.push('database');
        break;
      case 'user':
        values.login = value;
        matchedFields.push('user');
        break;
      case 'password':
        values.password = value;
        matchedFields.push('password');
        break;
      case 'sslmode': {
        const v = value.toLowerCase();
        values.ssl = v !== 'disable' && v !== 'disabled';
        if (values.ssl) matchedFields.push('SSL');
        break;
      }
      default:
        unmatchedKeys.push(key);
        break;
    }
  }

  return {
    format: 'libpq',
    engine: 'pgsql',
    values,
    matchedFields,
    unmatchedKeys,
  };
}
